import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronDown, Reply, Copy as CopyIcon, Forward, Star, Trash2, Info,
  Pin, RotateCcw, Smile, AlertCircle, CheckCheck, Check, Clock as ClockIcon,
  FileText, Download, CheckSquare, CornerUpRight, ImageOff, VideoOff, FileWarning,
} from 'lucide-react';
import { timeOnly, formatSize } from './utils';
import { setMyReaction, toggleStar, togglePin, markDeleted, copyToClipboard, isForwarded, stripForwardMarker } from './actions';
import MediaGallery from './MediaGallery';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const StatusTick = ({ status, error }) => {
  if (status === 'failed') {
    return (
      <span title={error || 'Delivery failed'} className="inline-flex items-center" data-testid="status-failed">
        <AlertCircle size={12} className="text-red-500" />
      </span>
    );
  }
  if (status === 'read') return <CheckCheck size={13} className="text-sky-500" />;
  if (status === 'delivered') return <CheckCheck size={13} className="text-gray-400" />;
  if (status === 'sent' || status === 'accepted') return <Check size={13} className="text-gray-400" />;
  return <ClockIcon size={12} className="text-gray-300" />;
};

const WorkflowBadge = ({ source, sourceName }) => {
  const map = {
    workflow: { label: 'Workflow', cls: 'bg-indigo-100 text-indigo-700' },
    lifecycle: { label: 'Auto', cls: 'bg-orange-100 text-orange-700' },
    broadcast: { label: 'Broadcast', cls: 'bg-purple-100 text-purple-700' },
    template: { label: 'Template', cls: 'bg-teal-100 text-teal-700' },
    bot: { label: 'Bot', cls: 'bg-sky-100 text-sky-700' },
    chatbot: { label: 'Bot', cls: 'bg-sky-100 text-sky-700' },
  };
  const meta = map[source];
  if (!meta) return null;
  return (
    <div className="flex items-center gap-1 mb-1" data-testid={`bubble-badge-${source}`}>
      <span className={`inline-flex items-center text-[9px] font-semibold tracking-wider px-1.5 py-0.5 rounded ${meta.cls}`}>
        {meta.label.toUpperCase()}
      </span>
      {sourceName && <span className="text-[10px] text-gray-400 truncate max-w-[140px]">{sourceName}</span>}
    </div>
  );
};

// ── Media fallback placeholders ─────────────────────────────────────────
// Production K8s containers have ephemeral disk, so previously-cached
// media files can vanish on pod restart → URL still in DB but 404 on fetch.
// Each renderer maintains its own broken-state to swap in a placeholder
// instead of breaking the bubble layout.

const BrokenPlaceholder = ({ icon: Icon, label, caption, fileName }) => (
  <div className="flex items-center gap-2.5 p-2.5 bg-amber-50 border border-amber-200 rounded-lg max-w-[260px]"
       data-testid="media-broken-placeholder">
    <div className="w-9 h-9 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
      <Icon size={18} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-amber-900 truncate">{fileName || label}</p>
      <p className="text-[10px] text-amber-700">Media unavailable</p>
      {caption && <p className="text-[11px] text-gray-700 mt-1 truncate">{caption}</p>}
    </div>
  </div>
);

const MediaImage = ({ url, caption }) => {
  const [broken, setBroken] = useState(false);
  if (broken) return <BrokenPlaceholder icon={ImageOff} label="Image" caption={caption} />;
  return (
    <div className="space-y-1.5">
      <img
        src={url}
        alt=""
        className="max-w-full rounded-lg"
        onError={() => setBroken(true)}
        data-testid="msg-media-image"
      />
      {caption && <p className="whitespace-pre-wrap text-sm leading-relaxed">{caption}</p>}
    </div>
  );
};

const MediaVideo = ({ url, caption }) => {
  const [broken, setBroken] = useState(false);
  if (broken) return <BrokenPlaceholder icon={VideoOff} label="Video" caption={caption} />;
  return (
    <div className="space-y-1.5">
      <video
        src={url}
        controls
        className="max-w-full rounded-lg"
        style={{ maxHeight: 300 }}
        onError={() => setBroken(true)}
        data-testid="msg-media-video"
      />
      {caption && <p className="whitespace-pre-wrap text-sm leading-relaxed">{caption}</p>}
    </div>
  );
};

const MediaAudio = ({ url }) => {
  const [broken, setBroken] = useState(false);
  if (broken) return <BrokenPlaceholder icon={FileWarning} label="Voice / Audio" />;
  return (
    <div className="space-y-1.5">
      <audio src={url} controls className="w-full" onError={() => setBroken(true)} data-testid="msg-media-audio" />
    </div>
  );
};

const MediaContent = ({ msg }) => {
  const type = msg.messageType || msg.type;
  const url = msg.mediaUrl || msg.media_url;
  const caption = msg.caption || (type !== 'text' ? null : '');

  if (type === 'image' && url) return <MediaImage url={url} caption={caption} />;
  if (type === 'video' && url) return <MediaVideo url={url} caption={caption} />;
  if (type === 'audio' && url) return <MediaAudio url={url} />;
  if (type === 'document' && url) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" download
        className="flex items-center gap-3 p-2.5 bg-black/5 rounded-lg hover:bg-black/10 transition-colors"
        data-testid="msg-media-document">
        <div className="w-9 h-9 rounded-md bg-red-50 text-red-500 flex items-center justify-center">
          <FileText size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{msg.fileName || 'Document'}</p>
          <p className="text-[11px] text-gray-500">{formatSize(msg.fileSize || 0)}</p>
        </div>
        <Download size={15} className="text-gray-500" />
      </a>
    );
  }
  // No URL but type is media → file likely never cached (e.g. token missing at receive time)
  if (['image', 'video', 'audio', 'document'].includes(type) && !url) {
    const iconMap = { image: ImageOff, video: VideoOff, audio: FileWarning, document: FileWarning };
    return <BrokenPlaceholder icon={iconMap[type] || FileWarning} label={type.charAt(0).toUpperCase() + type.slice(1)} caption={caption} fileName={msg.fileName} />;
  }
  return <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.message}</p>;
};

const ActionMenu = ({ mine, failed, onPick }) => {
  const items = [
    { id: 'reply', label: 'Reply', icon: Reply },
    { id: 'react', label: 'React', icon: Smile },
    { id: 'forward', label: 'Forward', icon: Forward },
    { id: 'copy', label: 'Copy', icon: CopyIcon },
    { id: 'star', label: 'Star', icon: Star },
    { id: 'select', label: 'Select', icon: CheckSquare },
    ...(mine ? [{ id: 'pin', label: 'Pin', icon: Pin }] : []),
    ...(mine ? [{ id: 'info', label: 'Info', icon: Info }] : []),
    ...(failed ? [{ id: 'retry', label: 'Retry Send', icon: RotateCcw, danger: true }] : []),
    { id: 'delete', label: 'Delete', icon: Trash2, danger: true },
  ];
  return (
    <div
      className="absolute z-30 min-w-[172px] rounded-xl bg-white border border-gray-200 shadow-2xl py-1.5 text-sm overflow-hidden animate-in fade-in slide-in-from-top-1"
      style={{ right: mine ? 0 : 'auto', left: mine ? 'auto' : 0, top: 'calc(100% + 4px)' }}
      data-testid="inbox-action-menu"
      onClick={(e) => e.stopPropagation()}
    >
      {items.map(({ id, label, icon: Icon, danger }) => (
        <button
          key={id}
          onClick={() => onPick(id)}
          className={`w-full flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 transition-colors
            ${danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700'}`}
          data-testid={`inbox-action-${id}`}
        >
          <Icon size={15} className={danger ? 'text-red-500' : 'text-gray-500'} />
          {label}
        </button>
      ))}
    </div>
  );
};

const QuickReactBar = ({ mine, onReact, onMore }) => (
  <div
    className="absolute z-20 flex items-center gap-0.5 rounded-full bg-white border border-gray-200 shadow-lg px-1 py-0.5"
    style={{
      top: -14, right: mine ? 8 : 'auto', left: mine ? 'auto' : 8,
    }}
    data-testid="inbox-quick-react"
    onClick={(e) => e.stopPropagation()}
  >
    {QUICK_REACTIONS.map(e => (
      <button
        key={e}
        onClick={() => onReact(e)}
        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-base leading-none transition-all hover:scale-125"
        data-testid={`inbox-react-${e}`}
      >
        {e}
      </button>
    ))}
    <button
      onClick={onMore}
      className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500"
      data-testid="inbox-react-more"
    >
      <Smile size={14} />
    </button>
  </div>
);

const ReactionsChip = ({ reactions, myReaction, onClick }) => {
  const entries = Object.entries(reactions || {}).filter(([, c]) => c > 0);
  if (!entries.length) return null;
  return (
    <button
      onClick={onClick}
      className="absolute -bottom-3 right-3 z-10 inline-flex items-center gap-0.5 bg-white border border-gray-200 rounded-full px-1.5 py-0.5 text-xs shadow-sm hover:shadow-md transition-shadow"
      data-testid="inbox-reactions-chip"
    >
      {entries.map(([e, c]) => (
        <span key={e} className={`inline-flex items-center ${myReaction === e ? 'scale-110' : ''}`}>
          <span className="leading-none">{e}</span>
          {c > 1 && <span className="ml-0.5 text-[10px] text-gray-600">{c}</span>}
        </span>
      ))}
    </button>
  );
};

const MessageBubble = ({
  msg, mine, groupItems = null,
  onRetry, onReply, onForward, onInfo, onStateChange,
  onSelect, selected, selectMode,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const rootRef = useRef(null);
  const ui = msg.__ui || {};
  const failed = mine && msg.status === 'failed';
  const hovered = menuOpen || quickOpen;

  // Close on outside click (menu OR quick reactions)
  useEffect(() => {
    if (!menuOpen && !quickOpen) return;
    const close = (e) => {
      if (!rootRef.current?.contains(e.target)) { setMenuOpen(false); setQuickOpen(false); }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen, quickOpen]);

  const pickAction = async (id) => {
    setMenuOpen(false);
    switch (id) {
      case 'reply': onReply?.(msg); break;
      case 'copy': await copyToClipboard(stripForwardMarker(msg.message || msg.caption || '')); break;
      case 'react': setQuickOpen(true); break;
      case 'forward': onForward?.([msg]); break;
      case 'star': onStateChange?.(msg.id, toggleStar(msg.id)); break;
      case 'pin': onStateChange?.(msg.id, togglePin(msg.id)); break;
      case 'info': onInfo?.(msg); break;
      case 'retry': onRetry?.(msg.id); break;
      case 'select': onSelect?.(msg.id, true); break;
      case 'delete': onStateChange?.(msg.id, markDeleted(msg.id)); break;
      default: break;
    }
  };

  const doReact = (emoji) => {
    const next = setMyReaction(msg.id, ui.myReaction === emoji ? null : emoji);
    onStateChange?.(msg.id, next);
    setQuickOpen(false);
  };

  const isGroup = Array.isArray(groupItems) && groupItems.length > 1;
  const hasMedia = !isGroup && ((msg.messageType && msg.messageType !== 'text') || msg.mediaUrl);
  const forwarded = isForwarded(msg.message);
  const displayText = forwarded ? stripForwardMarker(msg.message) : msg.message;

  // In select mode the whole bubble acts as a toggle target
  const handleBubbleClick = () => {
    if (selectMode) onSelect?.(msg.id);
  };

  return (
    <div
      ref={rootRef}
      className={`relative group ${mine ? 'self-end' : 'self-start'} max-w-[82%] md:max-w-[65%] ${ui.reactions && Object.keys(ui.reactions).length ? 'mb-4' : ''}`}
      onContextMenu={(e) => {
        if (selectMode) return;
        e.preventDefault();
        setMenuOpen(true); setQuickOpen(false);
      }}
      data-testid={`inbox-msg-${msg.id || ''}`}
    >
      {/* Quick reactions bar — opens ONLY on explicit React/emoji click */}
      {quickOpen && (
        <QuickReactBar
          mine={mine}
          onReact={doReact}
          onMore={() => { setQuickOpen(false); setMenuOpen(true); }}
        />
      )}

      {/* Selection checkbox — visible in select mode */}
      {selectMode && (
        <button
          onClick={() => onSelect?.(msg.id)}
          className={`absolute ${mine ? '-left-8' : '-right-8'} top-1/2 -translate-y-1/2 z-30 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all
            ${selected ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-gray-300'}`}
          data-testid={`inbox-select-${msg.id || ''}`}
        >
          {selected && <Check size={13} className="text-white" />}
        </button>
      )}

      <div
        onClick={handleBubbleClick}
        className={`relative px-3 py-2 rounded-2xl shadow-sm transition-all
          ${mine ? 'bg-[#d9fdd3] rounded-br-sm text-gray-900' : 'bg-white rounded-bl-sm text-gray-900'}
          ${selectMode ? 'cursor-pointer' : ''}
          ${selected ? 'ring-2 ring-emerald-500 ring-offset-1' : ''}`}
      >
        {/* Workflow / source badge */}
        {msg.source && !['manual', 'incoming'].includes(msg.source) && (
          <WorkflowBadge source={msg.source} sourceName={msg.sourceName} />
        )}

        {/* Forwarded label */}
        {forwarded && (
          <div className="flex items-center gap-1 text-[11px] italic text-gray-500 mb-0.5" data-testid="inbox-forwarded-label">
            <CornerUpRight size={11} />
            Forwarded
          </div>
        )}

        {/* Content */}
        {isGroup ? <MediaGallery items={groupItems} /> : (hasMedia ? <MediaContent msg={msg} /> : (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{displayText}</p>
        ))}

        {/* Meta row */}
        <div className="flex items-center justify-end gap-1 mt-1 -mb-0.5">
          {ui.pinned && <Pin size={10} className="text-gray-500" />}
          {ui.starred && <Star size={10} className="text-amber-500 fill-amber-400" />}
          <span className="text-[10px] text-gray-500 tabular-nums">{timeOnly(msg.createdAt)}</span>
          {mine && <StatusTick status={msg.status} error={msg.error} />}
        </div>

        {/* Reaction chip overlay */}
        <ReactionsChip reactions={ui.reactions} myReaction={ui.myReaction} onClick={() => setQuickOpen(true)} />

        {/* Hover action buttons — emoji + chevron. No auto-open on hover. */}
        {!selectMode && (
          <div
            className={`absolute top-1 ${mine ? 'left-1' : 'right-1'} z-40 flex items-center gap-0.5 transition-opacity
              ${hovered ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setQuickOpen(v => !v); setMenuOpen(false); }}
              className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:bg-black/5"
              title="Add reaction"
              data-testid={`inbox-emoji-btn-${msg.id || ''}`}
            >
              <Smile size={13} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); setQuickOpen(false); }}
              className="w-6 h-6 rounded-full flex items-center justify-center text-gray-500 hover:bg-black/5"
              title="Message actions"
              data-testid={`inbox-actions-toggle-${msg.id || ''}`}
            >
              <ChevronDown size={14} />
            </button>
          </div>
        )}

        {menuOpen && (
          <ActionMenu mine={mine} failed={failed} onPick={pickAction} />
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
